import { forwardRef, type HTMLAttributes } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

/**
 * Card — 기본 표면. spec §10 / styles.css `.card`.
 * `radius`로 small(12)·medium(16)·large(20) 선택. hover 시 shadow-md + translate-y(-2).
 */
const cardStyles = cva(
  // V3 애플 재질(D-05): 스프링 easing. 무대 라운드·깊이는 아래 variant 로 옵트인.
  ['bg-surface border border-border', 'transition-all ease-spring'],
  {
    variants: {
      radius: {
        sm: 'rounded-md',
        md: 'rounded-lg',
        lg: 'rounded-xl',
        stage: 'rounded-stage', // 히어로·완성 카드 (24px, 애플 무대)
      },
      padding: {
        none: 'p-0',
        sm: 'p-4',
        md: 'p-5',
        lg: 'p-6',
      },
      shadow: {
        none: '',
        sm: 'shadow-sm',
        md: 'shadow-md',
        lift: 'shadow-lift', // 부유하는 카드
        float: 'shadow-float', // 크게 뜬 표면 (완성·모달)
      },
      hoverable: {
        true: 'hover:shadow-lift hover:-translate-y-0.5 cursor-pointer',
        false: '',
      },
    },
    defaultVariants: {
      radius: 'md',
      padding: 'md',
      shadow: 'sm',
      hoverable: false,
    },
  },
)

export interface CardProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardStyles> {}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, radius, padding, shadow, hoverable, ...rest }, ref) => (
    <div
      ref={ref}
      className={cn(cardStyles({ radius, padding, shadow, hoverable }), className)}
      {...rest}
    />
  ),
)
Card.displayName = 'Card'

export function CardHeader({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('mb-3', className)} {...rest} />
}

export function CardTitle({ className, ...rest }: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3
      className={cn('text-[18px] font-bold tracking-tight text-text', className)}
      {...rest}
    />
  )
}

export function CardDescription({
  className,
  ...rest
}: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('text-[13px] text-text-2 leading-relaxed', className)} {...rest} />
}
